<script setup lang="ts" generic="T extends string | number">
import { ref, computed, onMounted, useId } from 'vue'

import Layout from '~/ui/Layout.vue'
import Spacer from '~/ui/Spacer.vue'
import Markdown from '~/ui/Markdown.vue'

// TODO: Make label prop non-optional. It'll be used with aria-labeledby.
const props = defineProps<{
  label?: string
  options: Record<T, string>
  autofocus?: true
}>()

const id = useId()

const keys = computed(() => Object.keys(props.options) as T[])

const model = defineModel<T | undefined>({ required: true })

const index = computed({
  get() {
    return model.value
      ? keys.value.indexOf(model.value)
      : undefined
  },
  set(newIndex) {
    model.value = newIndex
      ? keys.value[newIndex]
      : undefined
  }
})

const input = ref()

onMounted(() => {
  if (props.autofocus) input.value.focus()
})
</script>

<template>
  <!-- TODO(ally): Decide which label should label which control. Probably the label is for the slider, and the three buttons have their content as label. Open question: Is focusability necessary? -->
  <Layout
    stack
    no-gap
    :class="$style.slider"
    :style="`
      --step-size: calc(100% / ${keys.length + 2});
      --slider-width: calc(var(--step-size) * ${keys.length - 1} + 16px);
      --slider-opacity: ${index === undefined ? .03 : 1};
      --current-step: ${index === undefined ? keys.length - 1 : index};
    `"
  >
    <!-- Label -->

    <label
      :id
      :class="$style.label"
    >
      <slot
        v-if="$slots.label"
        name="label"
      />
      <template v-else>
        {{ label }}
      </template>
    </label>

    <!-- List of options -->

    <Layout
      flex
      no-gap
    >
      <button
        v-for="key in keys"
        :key="key"
        :class="[$style.key, { [$style.current]: key === model }]"
        style="flex-basis: var(--step-size); padding-bottom: 8px;"
        type="button"
        tabindex="-1"
        aria-hidden="true"
        @click="() => { model = key; input.focus(); }"
      >
        {{ key }}
      </button>
    </Layout>

    <!-- Slider -->

    <span style="position: relative;">
      <input
        ref="input"
        v-model="index"
        type="range"
        style="width: var(--slider-width); cursor: pointer;"
        :aria-labelledby="id"
        :max="keys.length - 1"
        :autofocus="autofocus || undefined"
      >
      <div :class="$style.range" />
    </span>
    <Spacer size-8 />

    <!-- Description of current option -->

    <span style="position: relative;">
      <span style="display: inline-flex; margin-right: -100%; width: 100%; visibility: hidden;">
        <span
          v-for="key in keys"
          :key="key"
          :class="$style.description"
          style="margin-right: -20%; --current-step: 0; color: magenta;"
        >
          <Markdown :md="options[model!]" />
        </span>
      </span>
      <span
        v-if="model !== undefined"
        style="position: absolute;"
        :class="$style.description"
      >
        <!-- For some reason, the linter complains that (Record<T, string>)[T] is not string... -->
        <!-- TODO: https://dev.funkwhale.audio/funkwhale/funkwhale/-/issues/2437 -->
        <!-- @vue-ignore -->
        <Markdown :md="options[model]" />
      </span>
    </span>
  </Layout>
</template>

<style module>
.slider {
  .label {
    margin-top: -18px;
    padding-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
  }

  .key {
    all: unset;
    cursor: pointer;
    opacity: .7;

    &.current {
      opacity: 1;
    }

    &:hover{
        color: var(--color-950);
    }
  }

  .description {
    font-size: 12px;
    font-weight: 600;
    width: 11rem;
    overflow: visible;
    transition: margin .2s;
    --inset: calc(var(--step-size) * var(--current-step));
    margin-left: var(--inset);
    margin-right: calc(0px - var(--inset));

    p {
      margin: 0;
      line-height: 1.5em;
    }
  }

  /* Fake slider */
  .range {
    width: calc(var(--step-size) * var(--current-step) + 4px);
    position: absolute;
    top: 6px;
    left: 2.5px;
    height: 8px;
    border-radius: 8px;
    background-color: var(--fw-blue-500);
    transition: all .1s;
    pointer-events: none;
    opacity: calc( .5 + var(--slider-opacity) / 2);
  }

  input[type="range"]::-moz-range-thumb {
    background-color: var(--fw-blue-500);
    transition: all .1s;
    pointer-events: none;
    opacity: var(--slider-opacity);
  }

  input[type="range"]::-moz-range-track {
    border-radius: 8px;
    background-color: var(--color-200);
    opacity: var(--slider-opacity);
  }
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none; /* Crucial for WebKit */
    background-color: var(--fw-blue-500);
    transition: all .1s;
    pointer-events: none;
    opacity: var(--slider-opacity);
  }

  input[type="range"]::-webkit-slider-runnable-track {
    border-radius: 8px;
  }

  &[disabled] * {
    pointer-events: none;
  }
}
</style>
